import { DOCUMENT } from "@angular/common";
import { AfterViewChecked, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { RegionEnum } from "@edp-enums/region.enum";
import { environment } from "@edp-environments/environment";
import { EdpUser } from "@edp-models/user.model";
import { AppBannerService } from "@edp-services/app-banner.service";
import { AppService } from "@edp-services/app.service";
import { LanguageService } from "@edp-services/language.service";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { PushNotificationService } from "@edp-services/push-notifications.service";
import { RegionService } from "@edp-services/region.service";
import { OnboardingService } from "@edp-services/onboarding.service";
import { GetOnboarding, GetOnboardingWhatsNew, SetCompleteOnboarding } from "@edp-store/actions/onboarding.actions";
import { getIframeModal } from "@edp-store/selectors/iframe.selectors";
import { showLoader } from "@edp-store/selectors/layout.selectors";
import { getOnboardingWhatsNew } from "@edp-store/selectors/onboaring.selectors";
import { getRatingStatus } from "@edp-store/selectors/rating.selectors";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { Subject, Subscription } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { GetBenefitsUsersDetails } from "@edp-store/actions/benefits.actions";
import { getListBenefitsUsersDetails } from "@edp-store/selectors/benefits.selectors";
import { BenefitsService } from "@edp-services/benefits.service";
import { GetServiceAnswers } from "@edp-store/actions/define-house.action";
import { getContract } from "@edp-store/selectors/contract.selectors";

declare var jic: any;

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnDestroy, AfterViewChecked, OnInit {
    public contracts: Contract[];
    public user: EdpUser;
    public loading = true;
    public loader$ = this.store.pipe(select(showLoader));
    public iframeModal$ = this.store.pipe(select(getIframeModal));
    public ratingStatus$ = this.store.pipe(select(getRatingStatus));
    public isApp = false;
    public showAppBanner = false;

    private routerSubscription: Subscription;
    private unsubscribe$: Subject<void> = new Subject();

    constructor(
        public readonly translate: TranslateService,
        public readonly appService: AppService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly cdRef: ChangeDetectorRef,
        private readonly operationHistoryService: OperationHistoryService,
        private readonly store: Store<IAppState>,
        private readonly benefitsService: BenefitsService,
        private readonly deviceDetector: DeviceDetectorService,
        private readonly pushNotification: PushNotificationService,
        private readonly appBannerService: AppBannerService,
        private readonly onboardingService: OnboardingService,
        private readonly renderer2: Renderer2,
        public readonly languageService: LanguageService,
        public readonly regionService: RegionService,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.isApp = environment.isApp;
        this.showAppBanner = this.appBannerService.show();

        this.store.pipe(takeUntil(this.unsubscribe$), select(getUser)).subscribe(user => {
            if (user) {
                this.store.dispatch(new GetOnboarding());
                this.store.dispatch(new GetOnboardingWhatsNew());
                this.store.dispatch(new GetBenefitsUsersDetails());
                this.user = user;
                const welcomeRoute = "/planeta-zero/bem-vindo";
                const subscriptionRoute = "/planeta-zero/inscricao";

                /* - Validating when user tries to acess welcome or subscription routes directly - */
                const canViewWelcome = !user.welcomeViewed;
                const canViewSubscription = !(user.edpESubViewed && user.edpESubscribed);

                if (this.isApp) {
                    this.setUserInApp();
                }
                const region = this.regionService.getRegion();
                this.appService.changeRegion(region);

                if (
                    (location.pathname.includes(welcomeRoute) && !canViewWelcome) ||
                    (location.pathname.includes(subscriptionRoute) && !canViewSubscription)
                ) {
                    this.router.navigate(["/"]);
                }
            }
            languageService.initLanguageTranslate();
        });
        if (!environment.production) {
            this.regionService.setCookieCurrentRegion();
        }

        this.store.pipe(takeUntil(this.unsubscribe$), select(getContract)).subscribe(contract => {
            if (contract) {
                this.store.dispatch(new GetServiceAnswers());
            }
        });
    }

    ngOnInit(): void {
        if (this.isApp) {
            this.appService.splashScreen();
            this.appService.statusBar();
            this.pushNotification.listenOpenedAppPush();
        }
        if (!this.regionService.getHiddenElements("pt.edp.edponline.chat")) {
            this.startChatBot();
        }
        if (this.regionService.getRegion() === RegionEnum.PT) {
            this.startOneTrust();
        }
        this.handleOnboaringWhatsNew();
    }

    ngAfterViewChecked() {
        this.changedRoute();
        this.cdRef.detectChanges();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private setUserInApp(): void {
        this.appService.setAppUser({
            userFirstName: this.user.firstname,
            userLastName: this.user.lastname,
            photoURL: this.user.photoURL,
        });
    }

    private handleOnboaringWhatsNew() {
        this.store
            .pipe(
                select(getOnboardingWhatsNew),
                filter(o => !!o),
                take(1)
            )
            .subscribe(whatsNew => {
                if (!this.isApp && this.onboardingService.getReadingWhatsNew(whatsNew.id)) {
                    return;
                }
                this.store.dispatch(new SetCompleteOnboarding(whatsNew.id, "reading"));
                this.onboardingService.setoperationHistoryService(whatsNew.id, whatsNew.type, "VIEW");
            });
    }

    startChatBot() {
        if (typeof jic === "undefined") {
            const tryTimeout = setTimeout(() => {
                this.startChatBot();
                clearTimeout(tryTimeout);
            }, 1000);
            return;
        }

        // if (this.isApp && this.deviceDetector.isMobile()) {
        if (this.isApp) {
            /* Works only in PROD */
            jic.closeOverlay("5c8289b88f26c0e6066a3332");
        } else {
            jic.showManualOverlay("5c8289b88f26c0e6066a3332");
        }
    }

    startOneTrust() {
        if (this.isApp || !environment.production) {
            return;
        }

        const scriptCDN = this.renderer2.createElement("script");
        scriptCDN.src = `https://cdn.cookielaw.org/scripttemplates/otSDKStub.js`;
        scriptCDN.type = `text/javascript`;
        scriptCDN.charset = `UTF-8`;
        scriptCDN.setAttribute("data-domain-script", "c205b3e2-d519-4db3-8665-d98878ed2721");
        this.renderer2.appendChild(this.document.body, scriptCDN);

        const scriptFunction = this.renderer2.createElement("script");
        scriptFunction.type = `text/javascript`;
        scriptFunction.text = `function OptanonWrapper() { }`;
        this.renderer2.appendChild(this.document.body, scriptFunction);
    }

    private changedRoute() {
        if (!this.routerSubscription) {
            this.routerSubscription = this.router.events
                .pipe(
                    takeUntil(this.unsubscribe$),
                    filter(event => event instanceof NavigationEnd)
                )
                .subscribe(value => {
                    this.registerOperation();
                });
        }
    }

    private registerOperation() {
        const getLastChild = (child: ActivatedRoute) => {
            return child.firstChild ? getLastChild(child.firstChild) : child;
        };
        const route: ActivatedRoute = getLastChild(this.activatedRoute);

        if (route.snapshot.data.operationHistory) {
            this.operationHistoryService.registerWithContract(null, route.snapshot.data.operationHistory);
        }
    }
}
